import { zodResolver } from '@hookform/resolvers/zod';
import {
  useCreateRole,
  useGetRole,
  useGetTenant,
  useListRoleAccessRules,
  useUpdateRole,
} from '@sunstone/api-client/hooks';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Label,
  Separator,
  Stepper,
  Textarea,
} from '@sunstone/ui';
import { Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';
import { PageHeader } from '@/components/ui/PageHeader';
import { useAuthToken } from '@/features/auth';
import { AccessSummary } from '@/features/users/AccessSummary';
import {
  accessRuleToDraft,
  type RuleDraft,
  ruleDraftConditionJson,
  ruleDraftHasContent,
  ruleDraftsEqual,
  splitRulesByKind,
} from '@/features/users/accessRuleDraft';
import { useTenantName } from '@/features/users/accessRuleSentence';
import { RoleAccessRulesEditor } from '@/features/users/RoleAccessRulesEditor';
import { PERMISSIONS_BY_RESOURCE_KIND, permissionActionLabel } from '@/lib/permissions';
import { routes } from '@/lib/routes';

const STEPS = ['Details', 'Permissions', 'Access', 'Confirm'];

const schema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string(),
  grantsAll: z.boolean(),
  permissions: z.array(z.string()),
});
type FormValues = z.infer<typeof schema>;

/**
 * Role create/edit — mirrors UserAccessWizard's shape (routed page,
 * PageHeader + Card + Stepper) but with 4 steps: Details, Permissions,
 * Access, Confirm — a role has no invitee-email step, so Confirm's own Save
 * button commits directly, in one CreateRole/UpdateRole call that also
 * carries any access_rules sub-changes (single transaction, single audit
 * event server-side). Handles both tenant-scoped and platform-scope roles
 * (tenantId presence, from the ?tenantId= query param at create time or the
 * fetched role's own tenantId at edit time) — never a system role, which
 * this page never creates or edits (Admin is permanently un-editable and
 * never created here).
 */
export function RoleAccessWizard() {
  const { roleId } = useParams<{ roleId?: string }>();
  const [searchParams] = useSearchParams();
  const isEditMode = !!roleId;
  const navigate = useNavigate();
  const { token } = useAuthToken();

  const { data: roleData, isLoading: roleLoading } = useGetRole(
    isEditMode ? (roleId ?? '') : '',
    token,
  );
  const existingRole = roleData?.role;
  const { data: rulesData, isLoading: rulesLoading } = useListRoleAccessRules(
    isEditMode ? (roleId ?? '') : '',
    token,
  );
  const existingRules = rulesData?.accessRules ?? [];

  const queryTenantId = searchParams.get('tenantId') ?? '';
  const tenantId = isEditMode ? (existingRole?.tenantId ?? '') : queryTenantId;
  const tenantName = useTenantName(tenantId);
  const { data: tenantData } = useGetTenant(tenantId, token);
  const isPartner = tenantId ? !tenantData?.tenant?.parentId : true;
  // A partner tenant owns no deployments/workspaces directly, only its
  // child tenants do — the resource pickers need parent_tenant_id, not
  // tenant_id, to see across the partner's whole portfolio. Only applies
  // when tenantId is a specific partner; a true platform-scope role
  // (tenantId empty) falls through to the caller's own accessible scope,
  // unaffected.
  const resourceParentTenantId = tenantId && isPartner ? tenantId : undefined;

  const permissionGroups = PERMISSIONS_BY_RESOURCE_KIND.filter((group) => {
    if (group.kind === 'partner') return !tenantId;
    if (group.kind === 'tenant') return !tenantId || isPartner;
    return true;
  });

  const [step, setStep] = useState(1);
  const [showRestriction, setShowRestriction] = useState(false);
  const [deploymentRules, setDeploymentRules] = useState<RuleDraft[]>([]);
  const [workspaceRules, setWorkspaceRules] = useState<RuleDraft[]>([]);
  const [seeded, setSeeded] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: { name: '', description: '', grantsAll: false, permissions: [] },
  });
  const name = form.watch('name');
  const description = form.watch('description');
  const grantsAll = form.watch('grantsAll');
  const permissions = form.watch('permissions');

  useEffect(() => {
    if (isEditMode && !seeded && existingRole && !rulesLoading) {
      form.setValue('name', existingRole.name);
      form.setValue('description', existingRole.description);
      form.setValue('grantsAll', existingRole.grantsAll);
      form.setValue('permissions', existingRole.permissions);
      const split = splitRulesByKind(existingRules);
      setDeploymentRules(split.deployment);
      setWorkspaceRules(split.workspace);
      if (split.deployment.length > 0 || split.workspace.length > 0) setShowRestriction(true);
      setSeeded(true);
    }
  }, [isEditMode, seeded, existingRole, rulesLoading, existingRules, form.setValue]);

  const createRole = useCreateRole(token);
  const updateRole = useUpdateRole(token);
  const [isSaving, setIsSaving] = useState(false);
  const submittedRef = useRef(false);

  function cancelRoute() {
    if (isEditMode) return routes.roleDetail(roleId ?? '', tenantId);
    return tenantId ? routes.tenant(tenantId) : routes.systemRoles();
  }

  function togglePermission(permission: string) {
    const current = form.getValues('permissions');
    form.setValue(
      'permissions',
      current.includes(permission)
        ? current.filter((p) => p !== permission)
        : [...current, permission],
    );
  }

  async function handleNextFromDetails() {
    const valid = await form.trigger(['name']);
    if (valid) setStep(2);
  }

  function draftToCreateParams(draft: RuleDraft) {
    return {
      effect: draft.effect,
      restrictionKind: draft.restrictionKind,
      resourceIds: draft.restrictionKind === 'id_based' ? draft.resourceIds : undefined,
      ruleConditionJson:
        draft.restrictionKind === 'rule_based' ? ruleDraftConditionJson(draft) : undefined,
      resourceKind: draft.resourceKind,
    };
  }

  function diffAccessRules() {
    const originalById = new Map(existingRules.map((r) => [r.id, r]));
    const allDrafts = [...deploymentRules, ...workspaceRules];
    const keptIds = new Set(allDrafts.map((d) => d.sourceId).filter((id): id is string => !!id));
    const toRevokeIds = existingRules.filter((r) => !keptIds.has(r.id)).map((r) => r.id);

    const rulesToCreate = [];
    for (const draft of allDrafts) {
      if (!ruleDraftHasContent(draft)) continue;
      const original = draft.sourceId ? originalById.get(draft.sourceId) : undefined;
      const changed = !original || !ruleDraftsEqual(draft, accessRuleToDraft(original));
      if (changed) {
        if (original) toRevokeIds.push(original.id);
        rulesToCreate.push(draftToCreateParams(draft));
      }
    }
    return { rulesToCreate, ruleIdsToRevoke: [...new Set(toRevokeIds)] };
  }

  async function saveEdits(values: FormValues) {
    if (!roleId || !existingRole) return;
    setIsSaving(true);
    try {
      const { rulesToCreate, ruleIdsToRevoke } = diffAccessRules();
      await updateRole.mutateAsync({
        id: roleId,
        name: values.name.trim(),
        description: values.description,
        permissions: values.grantsAll ? [] : values.permissions,
        grantsAll: values.grantsAll,
        rulesToCreate,
        ruleIdsToRevoke,
      });

      toast.success(`Role "${values.name.trim()}" updated`);
      navigate(cancelRoute());
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save role.');
    } finally {
      setIsSaving(false);
    }
  }

  async function createNewRole(values: FormValues) {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setIsSaving(true);
    try {
      const accessRules = [...deploymentRules, ...workspaceRules]
        .filter(ruleDraftHasContent)
        .map(draftToCreateParams);
      const created = await createRole.mutateAsync({
        tenantId: tenantId || undefined,
        name: values.name.trim(),
        description: values.description,
        permissions: values.grantsAll ? [] : values.permissions,
        grantsAll: values.grantsAll,
        accessRules,
      });
      const newRoleId = created.role?.id;

      toast.success(`Role "${values.name.trim()}" created`);
      navigate(newRoleId ? routes.roleDetail(newRoleId, tenantId) : cancelRoute());
    } catch (err) {
      submittedRef.current = false;
      toast.error(err instanceof Error ? err.message : 'Failed to create role.');
    } finally {
      setIsSaving(false);
    }
  }

  function onSubmit(values: FormValues) {
    if (isEditMode) {
      saveEdits(values);
    } else {
      createNewRole(values);
    }
  }

  if (isEditMode && (roleLoading || !existingRole || rulesLoading)) {
    return (
      <div className="flex min-h-[200px] items-center justify-center text-muted-foreground">
        Loading…
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <PageHeader
        title={
          isEditMode
            ? `Edit role — ${existingRole?.name}`
            : tenantId
              ? 'New role'
              : 'New platform role'
        }
        description={
          isEditMode
            ? "Change this role's details and access — changes take effect immediately for everyone who holds it."
            : tenantId
              ? 'A custom role for this customer.'
              : 'A custom role for platform-wide accounts — not visible to any tenant.'
        }
      />

      <Stepper steps={STEPS} current={step} />

      <Card className="max-w-2xl">
        <CardContent className="py-6">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && step < 4) e.preventDefault();
              }}
              className="space-y-6"
            >
              {step === 1 && (
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Name <span className="text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="e.g. Support Engineer" autoFocus />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            placeholder="What can someone with this role do?"
                            rows={2}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}

              {step === 2 && (
                <div className="space-y-4">
                  <div className="flex items-start gap-2 rounded-md border p-3">
                    <Checkbox
                      id="role-grants-all"
                      checked={grantsAll}
                      onCheckedChange={(checked) => form.setValue('grantsAll', checked === true)}
                      className="mt-0.5"
                    />
                    <div className="space-y-0.5">
                      <Label htmlFor="role-grants-all" className="cursor-pointer">
                        Grants all permissions
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Always has every permission, including ones added later. Doesn't affect
                        resource restrictions — this role can still be scoped with access rules.
                      </p>
                    </div>
                  </div>

                  <Separator />

                  {grantsAll ? (
                    <p className="text-sm text-muted-foreground">
                      Every permission is granted automatically — nothing to select below.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      <Label>Permissions</Label>
                      {permissionGroups.map((group) => (
                        <div key={group.kind} className="space-y-1.5">
                          <p className="text-xs font-medium text-muted-foreground">{group.label}</p>
                          <div className="grid grid-cols-3 gap-1.5">
                            {group.permissions.map((permission) => (
                              <div key={permission} className="flex items-center gap-2">
                                <Checkbox
                                  id={`role-perm-${permission}`}
                                  checked={permissions.includes(permission)}
                                  onCheckedChange={() => togglePermission(permission)}
                                />
                                <Label
                                  htmlFor={`role-perm-${permission}`}
                                  className="text-sm font-normal cursor-pointer"
                                >
                                  {permissionActionLabel(permission)}
                                </Label>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {step === 3 && (
                <RoleAccessRulesEditor
                  tenantId={tenantId}
                  parentTenantId={resourceParentTenantId}
                  roleName={name}
                  showRestriction={showRestriction}
                  onShowRestrictionChange={setShowRestriction}
                  deploymentRules={deploymentRules}
                  onDeploymentRulesChange={setDeploymentRules}
                  workspaceRules={workspaceRules}
                  onWorkspaceRulesChange={setWorkspaceRules}
                />
              )}

              {step === 4 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">
                      {isEditMode ? 'Review changes' : 'Review before creating'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-medium text-muted-foreground">Name</p>
                        <p className="text-sm">{name || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-muted-foreground">Description</p>
                        <p className="text-sm">{description || '—'}</p>
                      </div>
                    </div>

                    <Separator />

                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">Permissions</p>
                      {grantsAll ? (
                        <p className="text-sm">
                          Grants all permissions, including ones added later.
                        </p>
                      ) : permissions.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No permissions selected.</p>
                      ) : (
                        <div className="space-y-1">
                          {permissionGroups.map((group) => {
                            const selected = group.permissions.filter((p) =>
                              permissions.includes(p),
                            );
                            if (selected.length === 0) return null;
                            return (
                              <p key={group.kind} className="text-sm">
                                <span className="text-muted-foreground">{group.label}:</span>{' '}
                                {selected.map(permissionActionLabel).join(', ')}
                              </p>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <Separator />

                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">Access</p>
                      <AccessSummary
                        mode="role-only"
                        tenantId={tenantId}
                        tenantName={tenantName}
                        isPartner={isPartner}
                        deploymentRules={showRestriction ? deploymentRules : []}
                        workspaceRules={showRestriction ? workspaceRules : []}
                      />
                    </div>
                  </CardContent>
                </Card>
              )}

              <div className="flex items-center justify-between gap-3 pt-2">
                {step > 1 ? (
                  <Button type="button" variant="ghost" size="sm" onClick={() => setStep(step - 1)}>
                    ← Back
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(cancelRoute())}
                  >
                    Cancel
                  </Button>
                )}

                {step < 4 ? (
                  <Button
                    type="button"
                    size="sm"
                    onClick={step === 1 ? handleNextFromDetails : () => setStep(step + 1)}
                  >
                    Next
                  </Button>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    disabled={isSaving}
                    onClick={form.handleSubmit(onSubmit)}
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        Saving…
                      </>
                    ) : isEditMode ? (
                      'Save changes'
                    ) : (
                      'Create role'
                    )}
                  </Button>
                )}
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
